import styled from 'styled-components';
import { useContext } from 'react';

import Typography from '../../../kingpin/atoms/Typography';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import useValueFormatters from '../../../hooks/useValueFormatters';
import toSentenceCase from '../../../services/toSentenceCase';
import { BonusOverviewContext } from '../providers/BonusOverviewProvider';
import { isArnoldBrosRewardBands } from '../utils/rewardBandsUtils';

const Outer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 280px;
  background-color: var(--color-tooltip-bg);
  border-radius: 8px;
`;

const Section = styled.div`
  background-color: var(--color-tooltip-section-bg);
  padding: 16px;
  border-radius: 8px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--color-tooltip-divider);
`;

const SectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PayRow = styled.div<{ isTotal?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: ${({ isTotal }) => (isTotal ? '8px 0 0' : '0')};
  margin-top: ${({ isTotal }) => (isTotal ? '4px' : '0')};
  border-top: ${({ isTotal }) =>
    isTotal ? '1px solid var(--color-tooltip-divider)' : 'none'};
`;

/**
 * Tooltip content for an `aggregated` reward (e.g. Veriha fuel) whose sub-rewards are banded
 * (mpg, idle). Explains how each component came together to make the reward's total: for each,
 * the driver's value, the tier it hit, and its $ contribution — summing to the header total.
 */
const AggregatedRewardTooltip = ({
  reward,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatValue } = useValueFormatters();

  if (!bonusOverview) {
    return null;
  }

  const { dataset, rewardType } = bonusOverview.bonusProgram;

  const formatMoney = (value: number): string =>
    rewardType === 'points'
      ? `${formatValue({ value, formatting: { precision: 0 } })} PTS`
      : formatValue({
          value,
          formatting: { prefix: 'currency', precision: 2 },
        }).toString();

  const subRewards = ('rewards' in reward ? reward.rewards : undefined) ?? [];

  return (
    <Outer>
      <Section>
        <PayRow>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {`${toSentenceCase(reward.name)} total`}
          </Typography.Body>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {formatMoney(reward.reward ?? 0)}
          </Typography.Body>
        </PayRow>
      </Section>

      <Section>
        <SectionTitleRow>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            Sub-rewards
          </Typography.Body>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            Pay
          </Typography.Body>
        </SectionTitleRow>
        <SectionRows>
          {subRewards.map((sub, index) => {
            const bands = sub.rewardBands;
            if (!bands || isArnoldBrosRewardBands(bands)) {
              return null;
            }
            return (
              <PayRow key={sub.name}>
                <Typography.Body
                  type={'Body 13'}
                  color={'var(--color-tooltip-text)'}
                >
                  {`${index > 0 ? '+ ' : ''}${getFieldLabel({
                    field: bands.field,
                    dataType: dataset,
                  })}`}
                </Typography.Body>
                <Typography.Body
                  type={'Body 13 Bold'}
                  color={'var(--color-tooltip-text)'}
                >
                  {formatMoney(sub.reward ?? 0)}
                </Typography.Body>
              </PayRow>
            );
          })}
          <PayRow isTotal>
            <Typography.Body
              type={'Body 14 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              Total
            </Typography.Body>
            <Typography.Body
              type={'Body 14 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              {formatMoney(reward.reward ?? 0)}
            </Typography.Body>
          </PayRow>
        </SectionRows>
      </Section>
    </Outer>
  );
};

export default AggregatedRewardTooltip;
